import Phaser from 'phaser';
import { speakRoundIntro } from '@/game/roundIntro';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK, PAIRS } from '@/game/config';
import { getBackground, getBestPairs, setBestPairs, type Background } from '@/game/storage';
import { fableVariant } from '@/game/theme';
import { Starfield } from '@/game/objects/Starfield';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { addMuteToggle } from '@/game/ui/muteToggle';
import { wordTextStyle } from '@/game/fonts';
import { wordIconKey, wordIconUrl } from '@/game/wordIcons';
import { getPictureWordsPool } from '@/data/pools';
import { ReadingProgression } from '@/game/progression/ReadingProgression';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { maybeAskForRating } from '@/game/rating';
import { trackPairsStart, trackPairsEnd } from '@/analytics';
import {
  playPopSound,
  playThudSound,
  playRecordSound,
  playRoundCompleteSound,
  speakRulePrompt,
  startBackdropAmbience,
  stopBackdropAmbience,
} from '@/game/audio';
import {
  pairsForRound, gridFor, eligibleWords, pickPairs, dealDeck,
  type PairWord, type DealtCard,
} from '@/game/pairs/deal';
import { mismatchCostsBrain, brainsAfterRound } from '@/game/pairs/rules';
import { redirectIfWebLimit } from '@/game/ui/webLimitCard';

interface PairsInit {
  lang: string;
  /** Dev only (`?pairs=3`): start the ladder further up so the big boards can be seen. */
  startRound?: number;
}

/**
 * The play area the grid is laid out in — below the HUD, above the mute toggle.
 *
 * The top edge sits below the brain row, not just below the back pill: the brains moved under the
 * pill (§8.2) and, with the emoji's line box, their underside lands around y≈128 — which the old
 * y=120 grid put the first card row's left corner behind. The bottom stays at 660.
 */
const PLAY = { x: 90, y: 140, w: 1100, h: 520 };
const GUTTER = 14;

const BRAIN = '🧠';
const CARD_BACK_FILL = 0x243040;
const CARD_BACK_EDGE = 0x7ec4ff;
const CARD_FACE_FILL = 0xf2f6fa;
const CARD_MATCHED_FILL = 0xcfe3d2;

interface Card {
  data: DealtCard;
  root: Phaser.GameObjects.Container;
  back: Phaser.GameObjects.Container;
  face: Phaser.GameObjects.Container;
  /** Has this card ever been face-up? The whole brain rule turns on it — see pairs/rules.ts. */
  seen: boolean;
  faceUp: boolean;
  matched: boolean;
}

/**
 * Diamond Pairs — the Saturn reward minigame (specs/pairs-mode.md).
 *
 * Turn two cards: one carries a PICTURE, the other the WORD for it, so a pair can only be found by
 * reading. A mismatch costs a 🧠 only when both cards had already been seen (pairs/rules.ts). No
 * clock and no crumbling board — this is the calm treat, opposite Survival's reflex run.
 *
 * Self-contained like SurvivalScene: it touches neither RoundScene/BoardScene nor the Leitner
 * engine, and is entered only by spending a banked Diamond. What is on the board is decided by the
 * Phaser-free dealer in game/pairs/deal.ts; this scene only draws it.
 */
export class PairsScene extends Phaser.Scene {
  private lang!: string;
  private startRound = 1;

  private pool: PairWord[] = [];
  private used = new Set<string>();

  private round = 1;
  private brains = PAIRS.startBrains;
  private brainsLostThisRound = 0;
  private pairsFound = 0;
  private best = 0;

  private cards: Card[] = [];
  /** Texture keys of the baked card plates, one per (card size, colour) — see plateTexture. */
  private plateTexKeys: string[] = [];
  private first: Card | null = null;
  private firstWasSeen = false;
  private locked = true;      // stays locked until the how-to card is dismissed
  private over = false;

  private brainIcons: Phaser.GameObjects.Text[] = [];
  private roundText!: Phaser.GameObjects.Text;
  private foundText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'Pairs' });
  }

  init(data: PairsInit): void {
    this.lang = data.lang;
    this.startRound = Math.max(1, Math.floor(data.startRound ?? 1));
    this.round = this.startRound;
    this.brains = PAIRS.startBrains;
    this.brainsLostThisRound = 0;
    this.pairsFound = 0;
    this.cards = [];
    this.used = new Set();
    this.first = null;
    this.firstWasSeen = false;
    this.locked = true;
    this.over = false;
  }

  preload(): void {
    // The player's chosen backdrop, loaded on demand exactly as BoardScene does it. Unlike
    // Survival, Pairs has no exclusive backdrop: falling white words need a dark quiet field, a
    // card grid does not, and the calm treat is a good place to see the background you picked.
    const bg = getBackground();
    if (bg !== 'space') {
      const asset = backgroundAsset(bg);
      if (!this.textures.exists(`bg_${asset}`)) {
        this.load.image(`bg_${asset}`, `/images/background_${asset}.jpg`);
      }
    }
  }

  create(): void {
    if (redirectIfWebLimit(this)) return;   // free web build used up — see webLimitCard.ts
    this.best = getBestPairs();
    trackPairsStart();   // web demo only

    this.drawBackdrop();
    startBackdropAmbience(getBackground());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      stopBackdropAmbience();
      this.clearPlateTextures();
    });

    // A HAND, and deliberately NOT `body.in-game`. That class is one rule — `cursor: none` on the
    // canvas — and it exists so the drawn Crosshair can replace the pointer in the scenes you aim
    // in. Pairs has no crosshair, so wearing the class here just deleted the mouse pointer and put
    // nothing in its place. Nothing in this scene is aimed at: every part of it is tapped, cards
    // and tap-anywhere overlays alike, so the hand is the honest cursor for the whole scene.
    // (MenuScene.create sets it back to 'default' on the way out.)
    this.input.setDefaultCursor('pointer');

    this.pool = this.buildPool();
    this.drawHud();

    addMuteToggle(this, {
      depth: 50,
      onToggle: (muted) => {
        if (muted) stopBackdropAmbience();
        else startBackdropAmbience(getBackground());
      },
    });

    // Every run opens on the how-to card — a treat that turns up a handful of times over months is
    // never familiar, and it costs one tap. It is also the ONE place the mode is named (§8.1).
    this.showHowTo(() => this.startRoundNow(this.round));
  }

  // ── content ──────────────────────────────────────────────────────────────────

  /**
   * The words this run draws from: the picture pool for the player's language, ordered
   * "already-learned first" by the same routine Survival uses, so a child re-reads words they know.
   *
   * The PICTURE pool whatever lesson is active — a Diamond earned in the Fables journey still
   * opens a picture-word Pairs run, the same swap survivalPack() makes for banner-context lessons,
   * and for the same reason: this mode needs data of a shape only one pool has.
   */
  private buildPool(): PairWord[] {
    const pool = getPictureWordsPool(this.lang) ?? getPictureWordsPool('en');
    if (!pool) return [];
    // Read-only use: the constructor loads the mastery blob, and survivalWords() only sorts it.
    // beginSession() is deliberately NOT called — a reward must not write to the journey.
    const words = new ReadingProgression(this.lang, pool).survivalWords();
    return eligibleWords(words);
  }

  // ── rounds ───────────────────────────────────────────────────────────────────

  private startRoundNow(round: number): void {
    this.round = round;
    this.brainsLostThisRound = 0;
    this.first = null;
    this.locked = true;
    this.roundText.setText(i18next.t('pairs.round', { n: round }));
    // Pairs is the one mode that never said what it wanted out loud: the how-to card carries the
    // rule and then it is gone. The board itself has no ask — that is the design (the pair IS
    // the question) — so the instruction is a spoken one only.
    speakRoundIntro(i18next.t('pairs.intro'), this.lang, round);

    const pairs = pickPairs(this.pool, pairsForRound(round), this.used);
    // Pool exhausted (only after ~13 rounds on a 150-word pool): allow repeats rather than ending
    // a run the player has not lost.
    if (pairs.length < pairsForRound(round)) this.used.clear();
    for (const p of pairs) this.used.add(p.id);

    const deck = dealDeck(pairs);

    // Load only this round's icons — precached by the service worker, so a cache read rather than
    // a request (same load-what-you-show policy as BoardScene's picture banners).
    const missing = pairs.filter((p) => !this.textures.exists(wordIconKey(p.id)));
    if (missing.length === 0) {
      this.layoutRound(deck);
      return;
    }
    for (const p of missing) this.load.image(wordIconKey(p.id), wordIconUrl(p.id));
    // COMPLETE fires even when some files failed; a card whose texture is missing draws a
    // placeholder rather than an empty box (see makeCard).
    this.load.once(Phaser.Loader.Events.COMPLETE, () => this.layoutRound(deck));
    this.load.start();
  }

  private layoutRound(deck: DealtCard[]): void {
    for (const c of this.cards) c.root.destroy();
    this.cards = [];
    this.clearPlateTextures();   // the card size changes with the grid; last round's plates are dead

    const [cols, rows] = gridFor(this.round);
    const cellW = PLAY.w / cols;
    const cellH = PLAY.h / rows;
    const cardW = cellW - GUTTER;
    const cardH = cellH - GUTTER;

    deck.forEach((data, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = PLAY.x + cellW * (col + 0.5);
      const y = PLAY.y + cellH * (row + 0.5);
      const card = this.makeCard(data, x, y, cardW, cardH);
      this.cards.push(card);
      // Dealt in, with a stagger, so the board arrives rather than appearing.
      card.root.setScale(0.2).setAlpha(0);
      this.tweens.add({
        targets: card.root, scale: 1, alpha: 1, duration: 220, delay: i * 22, ease: 'Back.easeOut',
      });
    });

    this.time.delayedCall(deck.length * 22 + 260, () => { this.locked = false; });
  }

  /**
   * A card plate — the rounded rect a back, a face or a matched face is drawn on — baked once per
   * (size, colour) and worn as a sprite.
   *
   * The plates used to be a Graphics each, and a Graphics is re-tessellated on EVERY frame: Phaser
   * 3 caches no geometry, so a filled and a stroked rounded rect mean eight arcs walked at ~100
   * points apiece (`iterStep` 0.01 in GraphicsWebGLRenderer) plus two earcut passes, per plate per
   * frame. A full board is 24 cards with a back and a face each — 48 plates of that, every frame,
   * for shapes that only ever change colour. The whole deck now shares three textures.
   */
  private plateTexture(
    w: number, h: number, fill: number, fillAlpha: number, edge: number, edgeAlpha: number,
  ): string {
    // Rounded, because the grid divides the play area into fractional widths and a key of
    // `169.33333333333334` is one float wobble away from baking a second, identical texture.
    const cw = Math.round(w);
    const ch = Math.round(h);
    const key = `pairs-plate-${cw}x${ch}-${fill.toString(16)}-${edge.toString(16)}`;
    if (this.textures.exists(key)) return key;
    const pad = 2;   // the 2px edge straddles the path, so leave it room on the outside
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(fill, fillAlpha).fillRoundedRect(pad, pad, cw, ch, 14);
    g.lineStyle(2, edge, edgeAlpha).strokeRoundedRect(pad, pad, cw, ch, 14);
    g.generateTexture(key, cw + pad * 2, ch + pad * 2);
    g.destroy();
    this.plateTexKeys.push(key);
    return key;
  }

  /** Drop the baked plates; nothing in Phaser evicts a texture on its own. */
  private clearPlateTextures(): void {
    for (const key of this.plateTexKeys) this.textures.remove(key);
    this.plateTexKeys = [];
  }

  private makeCard(data: DealtCard, x: number, y: number, w: number, h: number): Card {
    const root = this.add.container(x, y);

    // ── back: identical for every card, or the game is over before it starts ──
    const backBg = this.add.image(0, 0, this.plateTexture(w, h, CARD_BACK_FILL, 0.97, CARD_BACK_EDGE, 0.75));
    const backGem = this.add.text(0, 0, '💎', {
      fontFamily: FONT_STACK, fontSize: `${Math.round(Math.min(w, h) * 0.34)}px`,
    }).setOrigin(0.5).setAlpha(0.55);
    const back = this.add.container(0, 0, [backBg, backGem]);

    // ── face: the picture, or the word ──
    const faceBg = this.add.image(0, 0, this.plateTexture(w, h, CARD_FACE_FILL, 0.97, 0x8fb6d8, 0.9));
    const face = this.add.container(0, 0, [faceBg]);

    if (data.kind === 'picture') {
      const key = wordIconKey(data.pairId);
      if (this.textures.exists(key)) {
        const img = this.add.image(0, 0, key);
        const size = Math.min(w, h) - 16;
        img.setDisplaySize(size, size);
        face.add(img);
      } else {
        face.add(this.add.text(0, 0, '🖼', {
          fontFamily: FONT_STACK, fontSize: `${Math.round(Math.min(w, h) * 0.4)}px`,
        }).setOrigin(0.5));
      }
    } else {
      // Auto-shrink to the card. The floor is PAIRS.minFontPx; words that would need less are kept
      // out of the deck as data (deal.ts eligibleWords + picturewords-cardfit.test.ts), so this
      // loop is the second line of defence, not the first.
      const label = this.add.text(0, 0, data.text, {
        fontFamily: FONT_STACK, fontSize: '34px', color: '#1a2430', align: 'center',
        ...wordTextStyle(this.lang), stroke: '#ffffff', strokeThickness: 0,
      }).setOrigin(0.5);
      let size = 34;
      while (size > PAIRS.minFontPx && label.width > w - 18) {
        size -= 2;
        label.setFontSize(size);
      }
      face.add(label);
    }
    face.setVisible(false);
    root.add([back, face]);

    // Explicit hit rectangle rather than setSize()+setInteractive(): a container's displayOrigin
    // quirk once left the lower half of a round hit-area un-clickable (see the note in
    // BoardScene.addHelpButton). The children are drawn around (0,0), so this matches what is seen.
    root.setInteractive(
      new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h),
      Phaser.Geom.Rectangle.Contains,
    );
    root.input!.cursor = 'pointer';

    const card: Card = { data, root, back, face, seen: false, faceUp: false, matched: false };
    root.on('pointerdown', () => this.onCardTap(card));
    return card;
  }

  // ── turns ────────────────────────────────────────────────────────────────────

  private onCardTap(card: Card): void {
    if (this.over || this.locked || card.faceUp || card.matched) return;

    const wasSeen = card.seen;
    card.seen = true;

    if (!this.first) {
      this.first = card;
      this.firstWasSeen = wasSeen;
      this.flip(card, true);
      return;
    }

    this.locked = true;
    const a = this.first;
    const b = card;
    this.first = null;
    this.flip(card, true, () => this.resolve(a, b, this.firstWasSeen, wasSeen));
  }

  private resolve(a: Card, b: Card, seenA: boolean, seenB: boolean): void {
    if (a.data.pairId === b.data.pairId) {
      this.onMatch(a, b);
      return;
    }

    if (mismatchCostsBrain(seenA, seenB)) this.loseBrain(a, b);
    if (this.over) return;

    // The wrong pair holds so a slow player can read the second card — but any tap cuts it short,
    // since making an impatient player wait for information they already have is only friction.
    let resolved = false;
    const flipBack = (): void => {
      if (resolved) return;
      resolved = true;
      this.input.off('pointerdown', flipBack);
      this.flip(a, false);
      this.flip(b, false, () => { if (!this.over) this.locked = false; });
    };
    this.time.delayedCall(PAIRS.mismatchMs, flipBack);
    this.input.on('pointerdown', flipBack);
  }

  private onMatch(a: Card, b: Card): void {
    a.matched = true;
    b.matched = true;
    for (const c of [a, b]) {
      c.root.disableInteractive();
      // Repaint the face plate green-ish: matched pairs stay on the board (the player keeps their
      // map of it) but must stop looking like something to turn.
      const bg = c.face.list[0] as Phaser.GameObjects.Image;
      const w = c.root.input?.hitArea.width ?? 0;
      const h = c.root.input?.hitArea.height ?? 0;
      bg.setTexture(this.plateTexture(w, h, CARD_MATCHED_FILL, 0.95, 0x4ecdc4, 0.95));
      this.tweens.add({ targets: c.root, scale: 1.08, duration: 130, yoyo: true, ease: 'Quad.easeOut' });
    }
    playPopSound();
    this.pairsFound += 1;
    this.foundText.setText(i18next.t('pairs.found', { n: this.pairsFound }));

    if (this.cards.every((c) => c.matched)) {
      this.time.delayedCall(400, () => this.completeRound());
      return;
    }
    this.locked = false;
  }

  private loseBrain(a: Card, b: Card): void {
    this.brains = Math.max(0, this.brains - 1);
    this.brainsLostThisRound += 1;
    this.paintBrains();
    playThudSound();
    for (const c of [a, b]) {
      this.tweens.add({
        targets: c.root, x: c.root.x + 7, duration: 55, yoyo: true, repeat: 2, ease: 'Sine.easeInOut',
      });
    }
    if (this.brains === 0) this.time.delayedCall(650, () => this.gameOver());
  }

  private completeRound(): void {
    const before = this.brains;
    this.brains = brainsAfterRound(this.brains, this.brainsLostThisRound, PAIRS.startBrains);
    const gained = this.brains > before;
    this.paintBrains();
    playRoundCompleteSound();

    const lines = [i18next.t('pairs.round', { n: this.round + 1 })];
    if (gained) lines.push(i18next.t('pairs.brainBack'));
    const banner = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, lines.join('\n'), {
      fontFamily: FONT_STACK, fontSize: '52px', color: '#ffd166',
      align: 'center', padding: { x: 30, y: 18 },
    }).setOrigin(0.5).setDepth(100);
    const bannerBg = addRoundedLabelBg(this, banner, { radius: 22 });

    this.time.delayedCall(1200, () => {
      banner.destroy();
      bannerBg.destroy();
      this.startRoundNow(this.round + 1);
    });
  }

  // ── flip ─────────────────────────────────────────────────────────────────────

  private flip(card: Card, up: boolean, onDone?: () => void): void {
    if (card.faceUp === up) { onDone?.(); return; }
    this.tweens.add({
      targets: card.root,
      scaleX: 0,
      duration: PAIRS.flipMs / 2,
      ease: 'Quad.easeIn',
      onComplete: () => {
        card.faceUp = up;
        card.back.setVisible(!up);
        card.face.setVisible(up);
        // The word card speaks when it turns — the mode's one teaching moment. The picture card
        // stays silent: saying the word would hand over the answer.
        if (up && card.data.kind === 'word') speakRulePrompt(card.data.text, this.lang);
        this.tweens.add({
          targets: card.root,
          scaleX: 1,
          duration: PAIRS.flipMs / 2,
          ease: 'Quad.easeOut',
          onComplete: () => onDone?.(),
        });
      },
    });
  }

  // ── HUD ──────────────────────────────────────────────────────────────────────

  private drawBackdrop(): void {
    const bg = getBackground();
    if (bg === 'space') {
      new Starfield(this);
      return;
    }
    const key = `bg_${backgroundAsset(bg)}`;
    if (this.textures.exists(key)) {
      const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, key);
      img.setScale(Math.max(GAME_WIDTH / img.width, GAME_HEIGHT / img.height));
      img.setDepth(-1);
    } else {
      new Starfield(this);
    }
  }

  private drawHud(): void {
    // Laid out like the round mode's HUD (HUDScene), corner for corner: the back pill top-left with
    // the lives directly under it, and the run's readouts stacked in the top-right. A child moving
    // between the lesson and this reward should not have to re-find what a life looks like or where
    // the round number lives — the brains are the hearts, in the hearts' place.
    const back = this.drawBackButton();

    // Brains under the back pill, measured off its height so resizing the pill can't overlap them
    // (HUDScene positions the hearts the same way). 32px at a 40px pitch keeps the row's underside
    // clear of PLAY.y — the top card row starts at y ≈ 127.
    const brainsY = back.y + back.height + 10;
    this.brainIcons = [];
    for (let i = 0; i < PAIRS.startBrains; i++) {
      this.brainIcons.push(this.add.text(back.x + i * 40, brainsY, BRAIN, {
        fontFamily: FONT_STACK, fontSize: '32px',
      }).setDepth(40));
    }
    this.paintBrains();

    // Round and pairs-found in the top-right corner, in the round/time/best stack's colours and
    // sizes. Plain text with no pill, again as in HUDScene: those readouts sit over the same photo
    // backdrops there, so a plate here would be a second style for the same job.
    this.roundText = this.add.text(GAME_WIDTH - 24, 20,
      i18next.t('pairs.round', { n: this.round }), {
        fontFamily: FONT_STACK, fontSize: '28px', color: '#ffd166',
      }).setOrigin(1, 0).setDepth(40);

    this.foundText = this.add.text(GAME_WIDTH - 24, 58,
      i18next.t('pairs.found', { n: 0 }), {
        fontFamily: FONT_STACK, fontSize: '24px', color: '#ffffff',
      }).setOrigin(1, 0).setDepth(40);
  }

  /** An emptied brain is the same glyph dimmed — there is no hollow-brain character (cf. ♥/♡). */
  private paintBrains(): void {
    this.brainIcons.forEach((b, i) => b.setAlpha(i < this.brains ? 1 : 0.3));
  }

  private drawBackButton(): Phaser.GameObjects.Text {
    const back = this.add.text(12, 16, `← ${i18next.t('nav.menu')}`, {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 20, y: 12 },
    }).setOrigin(0, 0).setDepth(50);
    // Tap target grown out to the canvas corner — at FIT scale on a phone the drawn pill is well
    // under Apple's 44pt minimum (same reasoning as HUDScene/SurvivalScene).
    back.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(
        -back.x, -back.y, back.width + back.x + 10, back.height + back.y + 10,
      ),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    addRoundedLabelBg(this, back);
    back.on('pointerover', () => back.setColor('#ffffff'));
    back.on('pointerout', () => back.setColor('#dfe6ee'));
    // Straight out, no confirm. Leaving does forfeit the Diamond (it was spent on entry), and this
    // used to ask — but a child who taps "back" has decided, and a two-button question they have to
    // read to get out of is the heavier cost. The pill is small, tucked in the corner, and nothing
    // else up here is tappable.
    back.on('pointerdown', () => this.scene.start('Menu'));
    return back;
  }

  // ── overlays ─────────────────────────────────────────────────────────────────

  private showHowTo(onStart: () => void): void {
    // Built to the round mode's help panel (HelpScene), plate for plate: the same 85% blackout, the
    // same gold-edged slate, gold title over white body, and the same blue button at the bottom.
    // That panel is where a child already meets "here is the rule, tap when you have it" — a
    // second, differently-dressed card for the same job would just read as a different app.
    const layer = this.add.container(0, 0).setDepth(200);
    const shade = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85,
    );
    shade.setInteractive();   // swallows taps meant for the cards underneath
    const panel = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 0.85, GAME_HEIGHT * 0.78, 0x1a1f25,
    ).setStrokeStyle(4, 0xffd166);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 210, i18next.t('pairs.howToTitle'), {
      fontFamily: FONT_STACK, fontSize: '64px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30, i18next.t('pairs.howToBody'), {
      fontFamily: FONT_STACK, fontSize: '28px', color: '#ffffff', align: 'center',
      wordWrap: { width: GAME_WIDTH * 0.72 }, lineSpacing: 8,
    }).setOrigin(0.5);
    const brains = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 90,
      BRAIN.repeat(PAIRS.startBrains), {
        fontFamily: FONT_STACK, fontSize: '38px',
      }).setOrigin(0.5);
    const start = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 200, i18next.t('pairs.howToStart'), {
      fontFamily: FONT_STACK, fontSize: '36px', color: '#ffffff',
      backgroundColor: '#7ec4ff', padding: { x: 40, y: 14 }, fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    layer.add([shade, panel, title, body, brains, start]);

    // Tap anywhere to start, behind a short guard so the tap that spent the Diamond can't skip the
    // card it just opened. The guard is a TIMESTAMP compared at tap time, not a delayed
    // registration: registering the listener inside a timer makes the only way out of this card
    // depend on the scene clock running, and a scene whose clock is stalled (a backgrounded tab is
    // the everyday case) would then be stuck on the card with no way forward.
    const openedAt = performance.now();
    const dismiss = (): void => {
      if (performance.now() - openedAt < 350) return;
      this.input.off('pointerdown', dismiss);
      layer.destroy(true);
      onStart();
    };
    this.input.on('pointerdown', dismiss);
  }

  // ── end of run ───────────────────────────────────────────────────────────────

  private gameOver(): void {
    if (this.over) return;
    this.over = true;
    this.locked = true;

    // Show what was where: the answer is the consolation for losing, and a child who never sees
    // the board resolved learns nothing from the run that ended.
    for (const c of this.cards) {
      c.root.disableInteractive();
      if (!c.faceUp) this.flip(c, true);
    }

    trackPairsEnd(this.pairsFound);   // web demo only

    const isRecord = this.pairsFound > this.best;
    if (isRecord) { setBestPairs(this.pairsFound); this.best = this.pairsFound; playRecordSound(); }
    else playRoundCompleteSound();

    const headline = isRecord ? i18next.t('pairs.newBest') : i18next.t('pairs.gameOver');
    const message = `${headline}\n${i18next.t('pairs.found', { n: this.pairsFound })}`;
    const overlay = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, message, {
      fontFamily: FONT_STACK,
      fontSize: '60px',
      color: isRecord ? '#4ecdc4' : '#ffd166',
      padding: { x: 34, y: 20 },
      align: 'center',
    }).setOrigin(0.5).setAlpha(0).setScale(0.8).setDepth(100);
    const overlayBg = addRoundedLabelBg(this, overlay, { radius: 24 });
    overlayBg.setAlpha(0).setScale(0.8);

    const cont = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 130, i18next.t('pairs.continue'), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 16, y: 9 },
    }).setOrigin(0.5).setDepth(100);
    addRoundedLabelBg(this, cont);

    this.tweens.add({ targets: [overlay, overlayBg], alpha: 1, scale: 1, duration: 300, ease: 'Back.easeOut' });

    // Tap anywhere to return, behind the same timestamp guard as the how-to card (and for the same
    // reason) so the tap that lost the last brain doesn't dismiss the card it just opened.
    const shownAt = performance.now();
    const leave = (): void => {
      if (performance.now() - shownAt < 900) return;
      this.input.off('pointerdown', leave);
      this.scene.start('Menu');
    };
    this.input.on('pointerdown', leave);

    // End-of-game asks (web: buy card, apps: rating) — same pair, same order as Survival.
    maybeShowBuyPrompt(this, { delayMs: 1500 });
    maybeAskForRating(this, { kind: 'pairs', pairs: this.pairsFound });
  }
}

/** Resolve a chosen background to its image asset slug (fable has day/night variants). */
function backgroundAsset(bg: Background): string {
  return bg === 'fable' ? fableVariant() : bg;
}
